import {
  guessMediaTypeOfUrl,
  isAudioOnlyMediaType,
  parseMediaTypeString,
  toMediaTypeString
} from "./chunk-PDD4MAGE.js";
import {
  getLogger
} from "./chunk-LFIBM62H.js";
import {
  toError
} from "./chunk-2GHSQBQ5.js";

// src/core/configured-adapter.ts
var ConfiguredAdapter = class {
  adapterClass;
  adapterConfig;
  constructor(adapterClass, ...config) {
    this.adapterClass = adapterClass;
    this.adapterConfig = config;
  }
};

// src/events/custom-event-emitter.ts
var CoreCustomEventEmitter = class {
  eventTarget;
  constructor(eventTarget) {
    this.eventTarget = eventTarget;
  }
  emit(type, ...args) {
    const customEvent = new CustomEvent(type, { bubbles: true, detail: args[0] });
    this.eventTarget.dispatchEvent(customEvent);
  }
};

// src/events/event-emitter.ts
import { EventEmitter } from "eventemitter3";
var logger = getLogger("CoreEvent");
var CoreEventEmitter = class {
  emitter = new EventEmitter();
  on(event, handler, context) {
    this.emitter.on(event, handler, context);
  }
  once(event, handler, context) {
    this.emitter.once(event, handler, context);
  }
  off(event, handler, context) {
    this.emitter.off(event, handler, context);
  }
  emit(event, ...args) {
    this.emitter.emit(event, ...args);
  }
  reset() {
    this.emitter.removeAllListeners();
  }
};
var MultiEmitEventEmitter = class extends CoreEventEmitter {
  emitters;
  constructor(emitters) {
    super();
    this.emitters = emitters;
  }
  emit(event, ...args) {
    if (event === "playercore.mediarangechange" /* MediaRangeChange */ || event === "playercore.subtitlescueupdate" /* SubtitlesCueUpdate */) {
      logger.debug("Emitting event:", event, ...args);
    } else if (event !== "playercore.timeupdate" /* TimeUpdate */ && event !== "playercore.scrubberupdate" /* ScrubberUpdate */) {
      logger.log("Emitting event:", event, ...args);
    }
    super.emit(event, ...args);
    this.emitters.forEach((emitter) => {
      emitter.emit(event, ...args);
    });
  }
  reset() {
    super.reset();
    this.emitters.forEach((emitter) => emitter instanceof CoreEventEmitter && emitter.reset());
  }
};

// src/display-modes/null-display-modes.ts
var NullDisplayModes = class {
  getFullscreenState() {
    return {
      isSupported: false,
      isAvailable: false,
      isActive: false,
      isNativeUI: false
    };
  }
  enterFullscreen() {
  }
  exitFullscreen() {
  }
  getPiPState() {
    return {
      isSupported: false,
      isAvailable: false,
      isActive: false
    };
  }
  enterPiP() {
  }
  exitPiP() {
  }
  getAirPlayState() {
    return {
      isSupported: false,
      isAvailable: false,
      isActive: false,
      isScanning: false
    };
  }
  startAirPlayTargetScan() {
  }
  stopAirPlayTargetScan() {
  }
  enterAirPlay() {
  }
  getRemotePlaybackState() {
    return {
      isSupported: false,
      isAvailable: false,
      isActive: false,
      isScanning: false
    };
  }
  startRemoteTargetScan() {
  }
  stopRemoteTargetScan() {
  }
  enterRemotePlayback() {
  }
  destroy() {
  }
};

// src/adapter/adapter-snapshot.ts
import { DestroyableBase } from "@nrk/destroyable";
var logger2 = getLogger("AdapterSnapshot");
var AdapterSnapshot = class _AdapterSnapshot extends DestroyableBase {
  values;
  constructor(values) {
    super();
    this.values = values;
  }
  /**
   * Grab all values from another adapter.
   */
  static from(other) {
    return new _AdapterSnapshot({
      adapterInfo: other.getAdapterInfo(),
      mediaInfo: other.getMediaInfo(),
      state: other.getState(),
      waitingReason: other.getWaitingReason(),
      pausedReason: other.getPausedReason(),
      error: other.getError(),
      liveStream: other.isLiveStream(),
      liveStreamEnded: other.isLiveStreamEnded(),
      seekable: other.isSeekable(),
      currentTime: other.getCurrentTime(),
      currentTimeLive: other.getCurrentTimeLive(),
      programDateTimeAtPosZero: other.positionToLiveTime(0).getTime() / 1e3,
      scrubberValues: other.getScrubberValues(),
      playbackRate: other.getPlaybackRate(),
      muted: other.isMuted(),
      volume: other.getVolume(),
      mediaRange: other.getMediaRange(),
      scrubberRange: other.getScrubberRange(),
      seekableRange: other.getSeekableRange()
    });
  }
  getAdapterInfo() {
    logger2.log("Getting adapter info from adapter snapshot.");
    return this.values.adapterInfo;
  }
  getMediaInfo() {
    logger2.log("Getting media info from adapter snapshot.");
    return this.values.mediaInfo;
  }
  preload() {
    logger2.log("Calling preload on adapter snapshot (no-op).");
    return Promise.resolve();
  }
  createTimeRangeEventTrack() {
    throw new Error("Can't create time range event track on adapter snapshot.");
  }
  play() {
    logger2.log("Calling play on adapter snapshot (no-op).");
    return Promise.resolve();
  }
  pause() {
    logger2.log("Calling pause on adapter snapshot (no-op).");
  }
  getState() {
    logger2.log("Getting state from adapter snapshot.");
    return this.values.state;
  }
  getWaitingReason() {
    logger2.log("Getting waiting state from adapter snapshot.");
    return this.values.waitingReason;
  }
  getPausedReason() {
    logger2.log("Getting paused state from adapter snapshot.");
    return this.values.pausedReason;
  }
  getError() {
    logger2.log("Getting error from adapter snapshot.");
    return this.values.error;
  }
  isLiveStream() {
    logger2.log("Getting live stream from adapter snapshot.");
    return this.values.liveStream;
  }
  isLiveStreamEnded() {
    logger2.log("Getting live stream ended from adapter snapshot.");
    return this.values.liveStreamEnded;
  }
  isSeekable() {
    logger2.log("Getting seekable from adapter snapshot.");
    return this.values.seekable;
  }
  seekTo() {
    logger2.log("Seeking on adapter snapshot (no-op).");
  }
  seekToRelativeTime() {
    logger2.log("Seeking on adapter snapshot (no-op).");
  }
  seekToLive() {
    logger2.log("Seeking on adapter snapshot (no-op).");
  }
  seekToLiveTime() {
    logger2.log("Seeking on adapter snapshot (no-op).");
  }
  getCurrentTime() {
    logger2.log("Getting current time from adapter snapshot.");
    return this.values.currentTime;
  }
  getCurrentTimeLive() {
    logger2.log("Getting current live time from adapter snapshot.");
    return this.values.currentTimeLive;
  }
  // Actually functional.
  positionToLiveTime(position) {
    return new Date((this.values.programDateTimeAtPosZero + position) * 1e3);
  }
  // Actually functional.
  liveTimeToPosition(liveTime) {
    return liveTime.getTime() / 1e3 - this.values.programDateTimeAtPosZero;
  }
  getScrubberValues() {
    logger2.log("Getting scrubber values from adapter snapshot.");
    return this.values.scrubberValues;
  }
  setPlaybackRate() {
    logger2.log("Setting playback rate on adapter snapshot (no-op).");
  }
  getPlaybackRate() {
    logger2.log("Getting playback rate from adapter snapshot.");
    return this.values.playbackRate;
  }
  resetPlaybackRate() {
    logger2.log("Resetting playback rate on adapter snapshot (no-op).");
  }
  mute() {
    logger2.log("Muting on adapter snapshot (no-op).");
  }
  unmute() {
    logger2.log("Unmuting on adapter snapshot (no-op).");
  }
  isMuted() {
    logger2.log("Getting muted from adapter snapshot.");
    return this.values.muted;
  }
  setVolume() {
    logger2.log("Setting volume on adapter snapshot (no-op).");
  }
  getVolume() {
    logger2.log("Getting volume from adapter snapshot.");
    return this.values.volume;
  }
  getMediaRange() {
    logger2.log("Getting media range from adapter snapshot.");
    return this.values.mediaRange;
  }
  getScrubberRange() {
    logger2.log("Getting scrubber range from adapter snapshot.");
    return this.values.scrubberRange;
  }
  getSeekableRange() {
    logger2.log("Getting seekable range from adapter snapshot.");
    return this.values.seekableRange;
  }
  selectNoSubtitles() {
    logger2.log("Selecting no subtitles on adapter snapshot (no-op).");
  }
  selectSubtitles() {
    logger2.log("Selecting subtitles on adapter snapshot (no-op).");
  }
  getActiveSubtitles() {
    logger2.log("Getting active subtitles from adapter snapshot.");
    return void 0;
  }
  getAvailableSubtitles() {
    logger2.log("Getting available subtitles from adapter snapshot.");
    return [];
  }
  canAddSubtitles() {
    logger2.log("Getting can add subtitles from adapter snapshot.");
    return false;
  }
  addSubtitles() {
    throw new Error("Can't add subtitles to the adapter snapshot.");
  }
  canControlSubtitlesDisplay() {
    logger2.log("Can control subtitles display on adapter snapshot.");
    return false;
  }
  isSubtitlesDisplayed() {
    logger2.log("Getting is subtitles displayed from adapter snapshot.");
    return false;
  }
  displaySubtitles() {
    logger2.log("Toggling display subtitles on adapter snapshot (no-op).");
  }
  destroy() {
    super.destroy();
    return Promise.resolve();
  }
};

// src/core/playback-session.ts
var logger3 = getLogger("PlaybackSession");
var PlaybackSession = class {
  // "Double" private to shield from TS and JS users.
  __adapter;
  __eventEmitter;
  __qualityTracker;
  __displayModes;
  // Events.
  on;
  off;
  once;
  // Adapter.
  getAdapterInfo;
  getMediaInfo;
  preload;
  createTimeRangeEventTrack;
  // Playback controls (implemented by adapter).
  play;
  pause;
  getState;
  getWaitingReason;
  getPausedReason;
  getError;
  isLiveStream;
  isLiveStreamEnded;
  isSeekable;
  seekTo;
  seekToRelativeTime;
  seekToLive;
  seekToLiveTime;
  getCurrentTime;
  getCurrentTimeLive;
  positionToLiveTime;
  liveTimeToPosition;
  getScrubberValues;
  setPlaybackRate;
  getPlaybackRate;
  resetPlaybackRate;
  mute;
  unmute;
  isMuted;
  setVolume;
  getVolume;
  getMediaRange;
  getScrubberRange;
  getSeekableRange;
  // Subtitles controls (implemented by adapter).
  selectNoSubtitles;
  selectSubtitles;
  getActiveSubtitles;
  getAvailableSubtitles;
  canAddSubtitles;
  addSubtitles;
  canControlSubtitlesDisplay;
  isSubtitlesDisplayed;
  displaySubtitles;
  // Display modes.
  getFullscreenState;
  enterFullscreen;
  exitFullscreen;
  getPiPState;
  enterPiP;
  exitPiP;
  getAirPlayState;
  startAirPlayTargetScan;
  stopAirPlayTargetScan;
  enterAirPlay;
  getRemotePlaybackState;
  startRemoteTargetScan;
  stopRemoteTargetScan;
  enterRemotePlayback;
  constructor(adapter, eventEmitter, externalEventEmitter, qualityTracker, displayModes) {
    this.__adapter = adapter;
    this.__eventEmitter = eventEmitter;
    this.__qualityTracker = qualityTracker;
    this.__displayModes = displayModes;
    const emitterMethods = ["on", "off", "once"];
    const adapterMethods = [
      "getAdapterInfo",
      "getMediaInfo",
      "preload",
      "createTimeRangeEventTrack",
      "play",
      "pause",
      "getState",
      "getWaitingReason",
      "getPausedReason",
      "getError",
      "isLiveStream",
      "isLiveStreamEnded",
      "isSeekable",
      "seekTo",
      "seekToRelativeTime",
      "seekToLive",
      "seekToLiveTime",
      "getCurrentTime",
      "setPlaybackRate",
      "getPlaybackRate",
      "resetPlaybackRate",
      "mute",
      "unmute",
      "isMuted",
      "setVolume",
      "getVolume",
      "getMediaRange",
      "getScrubberValues",
      "getScrubberRange",
      "getSeekableRange",
      "selectNoSubtitles",
      "selectSubtitles",
      "getActiveSubtitles",
      "getAvailableSubtitles",
      "canAddSubtitles",
      "addSubtitles",
      "canControlSubtitlesDisplay",
      "isSubtitlesDisplayed",
      "displaySubtitles",
      "getCurrentTimeLive",
      "positionToLiveTime",
      "liveTimeToPosition"
    ];
    const displayModeMethods = [
      "getFullscreenState",
      "enterFullscreen",
      "exitFullscreen",
      "getPiPState",
      "enterPiP",
      "exitPiP",
      "getAirPlayState",
      "startAirPlayTargetScan",
      "stopAirPlayTargetScan",
      "enterAirPlay",
      "getRemotePlaybackState",
      "startRemoteTargetScan",
      "stopRemoteTargetScan",
      "enterRemotePlayback"
    ];
    emitterMethods.forEach((method) => {
      Object.defineProperty(this, method, {
        value: externalEventEmitter[method].bind(externalEventEmitter)
      });
    });
    adapterMethods.forEach((method) => {
      Object.defineProperty(this, method, {
        // @ts-expect-error - Length of args.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        value: (...args) => this.__adapter[method](...args)
      });
    });
    displayModeMethods.forEach((method) => {
      Object.defineProperty(this, method, {
        value: displayModes[method].bind(displayModes)
      });
    });
    eventEmitter.on("playercore.playingchange" /* PlayingChange */, (event) => {
      if (event.type === "replay" /* Replay */) {
        const dispatch = () => qualityTracker.dispatchQualityChangeEvent();
        eventEmitter.once("playercore.timerunningchange" /* TimeRunningChange */, dispatch);
        eventEmitter.once("playercore.qualitychange" /* QualityChange */, () => {
          eventEmitter.off("playercore.timerunningchange" /* TimeRunningChange */, dispatch);
        });
      }
    });
    eventEmitter.once("playercore.broken" /* Broken */, () => {
      this.close("broken" /* Broken */).catch((err) => {
        logger3.error("Failed to close session:", err);
      });
    });
    adapter.addEventListener("destroy", () => {
      this.close(adapter.destroyedReason).catch((err) => {
        logger3.error("Failed to close session:", err);
      });
    });
    eventEmitter.emit("playercore.sessioncreated" /* SessionCreated */, { session: this });
  }
  // Convenience methods, only using information from the adapter.
  /**
   * Returns true if playback has not been attempted yet.
   *
   * @category Convenience
   */
  isIdle() {
    return this.getState() === "idle" /* Idle */;
  }
  /**
   * Returns true if playback is happening or attempted.
   *
   * @category Convenience
   */
  isPlaying() {
    return this.getState() === "playing" /* Playing */;
  }
  /**
   * Returns true if playback is attempted but time is not running for some
   * reason, or if seeking while paused.
   *
   * @category Convenience
   */
  isWaiting() {
    return this.getWaitingReason() != void 0;
  }
  /**
   * Returns true if the player is actually playing.
   *
   * @category Convenience
   */
  isTimeRunning() {
    return this.isPlaying() && !this.isWaiting();
  }
  /**
   * Returns true if the player is in the broken state.
   *
   * @category Convenience
   */
  isBroken() {
    return this.getState() === "broken" /* Broken */;
  }
  // Other methods.
  /**
   * Returns the current quality of a variable bitrate stream.
   */
  getCurrentQuality() {
    return this.__qualityTracker.getCurrentQuality();
  }
  /**
   * Close the session, release resources and drop event listeners. The state
   * of the session is still available, but all action methods are no-ops.
   */
  async close(reason) {
    if (this.__adapter instanceof AdapterSnapshot) {
      return;
    }
    logger3.log("Closing session. Reason:", reason);
    const adapter = this.__adapter;
    this.__adapter = AdapterSnapshot.from(adapter);
    if (!adapter.destroyed) {
      await adapter.destroy();
    }
    this.__displayModes.destroy();
    this.__eventEmitter.emit("playercore.sessionclosed" /* SessionClosed */, { session: this, reason });
    this.__eventEmitter.reset();
  }
};

// src/core/quality-tracker.ts
var QualityTracker = class {
  emitter;
  availableQualities = [];
  currentQuality = void 0;
  constructor(emitter) {
    this.emitter = emitter;
  }
  setAvailableQualities(qualities) {
    this.availableQualities = qualities;
  }
  setCurrentQualityByLevel(level) {
    const quality = this.availableQualities.find((q) => q.level === level);
    if (quality) {
      this.setCurrentQuality(quality);
    }
  }
  setCurrentQualityByBitrate(bitrate) {
    const quality = this.availableQualities.find((q) => q.bitrate === bitrate || q.averageBitrate === bitrate);
    if (quality) {
      this.setCurrentQuality(quality);
    }
  }
  setCurrentQualityByVideoProps(width, height, frameRate) {
    const quality = this.availableQualities.find(
      (q) => q.width === width && q.height === height && (frameRate === void 0 || q.frameRate === frameRate)
    );
    if (quality) {
      this.setCurrentQuality(quality);
    }
  }
  setCurrentQuality(quality) {
    if (this.currentQuality !== quality) {
      this.currentQuality = quality;
      this.dispatchQualityChangeEvent();
    }
  }
  getCurrentQuality() {
    return this.currentQuality;
  }
  /**
   * For internal use, except in special cases where it makes sense to
   * re-announce the current quality level (read: replay);
   */
  dispatchQualityChangeEvent() {
    if (this.currentQuality) {
      this.emitter.emit("playercore.qualitychange" /* QualityChange */, this.currentQuality);
    }
  }
};

// src/events/scrubber-update-events.ts
var logger4 = getLogger("ScrubberUpdate");
var LIVE_SCRUBBER_UPDATE_INTERVAL = 250;
var ScrubberUpdateEvents = class {
  session;
  eventEmitter;
  interval;
  intervalId;
  constructor(session, eventEmitter, interval) {
    this.session = session;
    this.eventEmitter = eventEmitter;
    this.interval = interval ?? LIVE_SCRUBBER_UPDATE_INTERVAL;
    if (session.isTimeRunning()) {
      this.start();
    } else {
      eventEmitter.once("playercore.timerunningchange" /* TimeRunningChange */, () => this.start());
    }
    eventEmitter.on("playercore.broken" /* Broken */, () => this.destroy());
    eventEmitter.on("playercore.sessionclosed" /* SessionClosed */, () => this.destroy());
  }
  start() {
    if (this.session.isLiveStream() && !this.session.isLiveStreamEnded()) {
      logger4.log("Emitting scrubber update events by interval timer.");
      this.eventEmitter.emit("playercore.scrubberupdate" /* ScrubberUpdate */, this.session.getScrubberValues());
      this.intervalId = setInterval(() => {
        this.eventEmitter.emit("playercore.scrubberupdate" /* ScrubberUpdate */, this.session.getScrubberValues());
      }, this.interval);
      this.eventEmitter.once("playercore.livestreamended" /* LiveStreamEnded */, () => {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = void 0;
        }
        logger4.log("Changing to piggybacking on time updates.");
        this.eventEmitter.on("playercore.timeupdate" /* TimeUpdate */, () => {
          this.eventEmitter.emit("playercore.scrubberupdate" /* ScrubberUpdate */, this.session.getScrubberValues());
        });
      });
    } else {
      logger4.log("Emitting scrubber update events by piggybacking on time updates.");
      this.eventEmitter.on("playercore.timeupdate" /* TimeUpdate */, () => {
        this.eventEmitter.emit("playercore.scrubberupdate" /* ScrubberUpdate */, this.session.getScrubberValues());
      });
    }
  }
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = void 0;
    }
  }
};

// src/display-modes/fullscreen.ts
import { DestroyableSet, destroyableEventListener as addEventListener } from "@nrk/destroyable";
var logger5 = getLogger("Fullscreen");
function getSupportedIntegration() {
  const integrations = [
    {
      type: "standard",
      changeEvent: "fullscreenchange",
      isEnabled: () => document.fullscreenEnabled,
      requestFullscreen: (element) => element.requestFullscreen(),
      exitFullscreen: () => document.exitFullscreen(),
      getActiveElement: () => document.fullscreenElement || void 0
    },
    {
      type: "webkit",
      changeEvent: "webkitfullscreenchange",
      isEnabled: () => document.webkitFullscreenEnabled,
      requestFullscreen: (element) => element.webkitRequestFullscreen(),
      exitFullscreen: () => document.webkitExitFullscreen(),
      getActiveElement: () => document.webkitFullscreenElement
    },
    {
      type: "moz",
      changeEvent: "mozfullscreenchange",
      isEnabled: () => document.mozFullScreenEnabled,
      requestFullscreen: (element) => element.mozRequestFullScreen(),
      exitFullscreen: () => document.mozCancelFullScreen(),
      getActiveElement: () => document.mozFullScreenElement
    },
    {
      type: "ms",
      changeEvent: "MSFullscreenChange",
      isEnabled: () => document.msFullscreenEnabled,
      requestFullscreen: (element) => element.msRequestFullscreen(),
      exitFullscreen: () => document.msExitFullscreen(),
      getActiveElement: () => document.msFullscreenElement
    }
  ];
  return integrations.find((integration) => integration.isEnabled());
}
var Fullscreen = class extends DestroyableSet {
  element;
  stateChangedCallback;
  integration;
  supportsOrientationLock;
  isActive = false;
  constructor(element, stateChangedCallback) {
    super();
    const integration = getSupportedIntegration();
    if (!integration) {
      throw new Error("Fullscreen not supported!");
    }
    logger5.log(`HTML5 fullscreen integration: ${integration.type}.`);
    this.element = element;
    this.stateChangedCallback = stateChangedCallback;
    this.integration = integration;
    this.supportsOrientationLock = // eslint-disable-next-line compat/compat
    typeof window.screen.orientation?.type === "string" && window.screen.orientation.lock !== void 0;
    this.addDestroyable(addEventListener(document, integration.changeEvent, this.checkIfActive.bind(this)));
    this.checkIfActive();
  }
  static isSupported() {
    return getSupportedIntegration() !== void 0;
  }
  getFullscreenState() {
    return {
      isSupported: true,
      isAvailable: true,
      isActive: this.isActive,
      isNativeUI: false
    };
  }
  enterFullscreen(options) {
    Promise.resolve(this.integration.requestFullscreen(this.element)).then(() => this.attemptOrientationLock(options?.orientation)).catch((e) => {
      logger5.warn("Fullscreen request rejected:", e);
    });
  }
  exitFullscreen() {
    Promise.resolve(this.integration.exitFullscreen()).catch((e) => {
      logger5.warn("Failed to exit fullscreen:", e);
    });
  }
  checkIfActive() {
    const isActive = this.integration.getActiveElement() === this.element;
    if (isActive !== this.isActive) {
      this.isActive = isActive;
      this.stateChangedCallback("isActive");
    }
  }
  async attemptOrientationLock(orientation) {
    if (!this.supportsOrientationLock || !orientation) {
      return;
    }
    logger5.log(`Attempting to lock screen orientation to '${orientation}'.`);
    try {
      await window.screen.orientation.lock(orientation);
    } catch (e) {
      const error = toError(e);
      if (error.name === "NotSupportedError") {
        logger5.log("Screen orientation lock is not actually available.");
        this.supportsOrientationLock = false;
      } else {
        logger5.log(`Orientation error: ${error.name.replace("Error", "")}. ${error.message}`);
      }
    }
  }
};

// src/display-modes/native-remote-playback.ts
import {
  DestroyableSet as DestroyableSet2,
  destroyableTimeout as startTimeout,
  destroyableEventListener as addEventListener2
} from "@nrk/destroyable";
var logger6 = getLogger("NativeRemotePlayback");
var NativeRemotePlayback = class extends DestroyableSet2 {
  remote;
  stateChangedCallback;
  scannerId;
  scannerTimeout;
  isScanningSupported = true;
  hasMedia = false;
  hasTargets = false;
  constructor(media, stateChangedCallback) {
    super();
    if (!media.remote) {
      throw new Error("RemotePlayback not supported!");
    }
    this.remote = media.remote;
    this.stateChangedCallback = stateChangedCallback;
    const isActiveChanged = this.stateChangedCallback.bind(this, "isActive");
    this.addDestroyables([
      addEventListener2(this.remote, "connect", isActiveChanged),
      addEventListener2(this.remote, "connecting", isActiveChanged),
      addEventListener2(this.remote, "disconnect", isActiveChanged),
      addEventListener2(this.remote, "loadstart", () => {
        this.hasMedia = true;
        if (this.hasTargets) {
          this.stateChangedCallback("isAvailable");
        }
      }),
      addEventListener2(this.remote, "emptied", () => {
        this.hasMedia = false;
        if (this.hasTargets) {
          this.stateChangedCallback("isAvailable");
        }
      })
    ]);
    this.addEventListener("destroy", () => this.stopRemoteTargetScan());
  }
  static isSupported(media) {
    return "remote" in media && !media.disableRemotePlayback;
  }
  getRemotePlaybackState() {
    return {
      isSupported: true,
      isAvailable: this.hasMedia && this.hasTargets,
      isActive: this.remote.state === "connected" || this.remote.state === "connecting",
      isScanning: this.scannerId !== void 0
    };
  }
  startRemoteTargetScan(options) {
    if (this.isScanningSupported && this.scannerId === void 0) {
      this.scannerId = Number.NaN;
      this.remote.watchAvailability((hasTargets) => {
        logger6.log("Scan result:", hasTargets ? "available" : "not available");
        if (this.hasTargets !== hasTargets) {
          this.hasTargets = hasTargets;
          if (this.hasMedia) {
            this.stateChangedCallback("isAvailable");
          }
          if (hasTargets && options?.stopWhenAvailable) {
            logger6.log(`Stopping scan because targets.`);
            this.stopRemoteTargetScan();
          }
        }
      }).then((scannerId) => {
        if (this.scannerId === void 0) {
          logger6.log("Scanning cancelled before starting.");
          this.remote.cancelWatchAvailability(scannerId).catch((e) => {
            logger6.warn("Failed to stop scanning:", e);
          });
        } else {
          this.scannerId = scannerId;
          this.stateChangedCallback("isScanning");
          if (options?.timeout) {
            logger6.log(`Scanning for max ${options.timeout} ms.`);
            this.scannerTimeout = startTimeout(() => this.stopRemoteTargetScan(), options.timeout);
          }
        }
      }).catch(() => {
        logger6.log("Scanning is not supported.");
        this.isScanningSupported = false;
        this.scannerId = void 0;
        this.hasTargets = true;
        if (this.hasMedia) {
          this.stateChangedCallback("isAvailable");
        }
      });
    }
  }
  stopRemoteTargetScan() {
    if (this.scannerTimeout !== void 0) {
      this.scannerTimeout.destroy();
      this.scannerTimeout = void 0;
    }
    if (this.scannerId !== void 0) {
      if (!Number.isNaN(this.scannerId)) {
        this.remote.cancelWatchAvailability(this.scannerId).catch((e) => {
          logger6.warn("Failed to stop scanning:", e);
        });
      }
      this.scannerId = void 0;
      this.stateChangedCallback("isScanning");
    }
  }
  enterRemotePlayback() {
    this.remote.prompt().catch((e) => {
      logger6.warn("Remote playback prompt rejected:", e);
    });
  }
};

// src/display-modes/pip.ts
import { DestroyableSet as DestroyableSet3, destroyableEventListener as addEventListener3 } from "@nrk/destroyable";
var logger7 = getLogger("PiP");
var PiP = class extends DestroyableSet3 {
  video;
  stateChangedCallback;
  isAvailable = false;
  constructor(video, stateChangedCallback) {
    super();
    this.video = video;
    this.stateChangedCallback = stateChangedCallback;
    this.addDestroyables([
      addEventListener3(video, "enterpictureinpicture", stateChangedCallback.bind(stateChangedCallback, "isActive")),
      addEventListener3(video, "leavepictureinpicture", stateChangedCallback.bind(stateChangedCallback, "isActive")),
      addEventListener3(video, "loadedmetadata", this.checkIfAvailable.bind(this)),
      addEventListener3(video, "emptied", this.checkIfAvailable.bind(this))
    ]);
    this.checkIfAvailable();
  }
  static isSupported() {
    return "pictureInPictureEnabled" in document;
  }
  getPiPState() {
    return {
      isSupported: true,
      isAvailable: this.isAvailable,
      isActive: this.isActive()
    };
  }
  enterPiP() {
    Promise.resolve(this.video.requestPictureInPicture?.()).catch((e) => {
      logger7.warn("PiP request rejected:", e);
    });
  }
  exitPiP() {
    Promise.resolve(document.exitPictureInPicture?.()).catch((e) => {
      logger7.warn("Failed to exit PiP:", e);
    });
  }
  isActive() {
    return document.pictureInPictureElement === this.video;
  }
  checkIfAvailable() {
    const supportEnabled = !!document.pictureInPictureEnabled;
    const isAvailable = supportEnabled && !this.video.disablePictureInPicture && this.video.readyState !== 0;
    if (this.isAvailable !== isAvailable) {
      this.isAvailable = isAvailable;
      this.stateChangedCallback("isAvailable");
    }
  }
};

// src/display-modes/webkit-airplay.ts
import {
  DestroyableSet as DestroyableSet4,
  destroyableTimeout as startTimeout2,
  destroyableEventListener as addEventListener4
} from "@nrk/destroyable";
var logger8 = getLogger("AirPlay");
var WebkitAirPlay = class extends DestroyableSet4 {
  media;
  stateChangedCallback;
  scannerHooks;
  hasMedia = false;
  hasTargets = false;
  isActive = false;
  static isSupported() {
    return !!window.WebKitPlaybackTargetAvailabilityEvent;
  }
  constructor(media, stateChangedCallback) {
    super();
    this.media = media;
    this.stateChangedCallback = stateChangedCallback;
    this.addDestroyables([
      addEventListener4(media, "webkitcurrentplaybacktargetiswirelesschanged", this.checkIfActive.bind(this)),
      addEventListener4(media, "loadstart", this.checkIfHasMedia.bind(this)),
      addEventListener4(media, "emptied", this.checkIfHasMedia.bind(this))
    ]);
    this.addEventListener("destroy", () => this.stopAirPlayTargetScan());
    this.checkIfActive();
  }
  getAirPlayState() {
    return {
      isSupported: true,
      isAvailable: this.hasMedia && this.hasTargets,
      isActive: this.isActive,
      isScanning: this.scannerHooks !== void 0
    };
  }
  startAirPlayTargetScan(options) {
    if (this.scannerHooks === void 0) {
      this.scannerHooks = new DestroyableSet4();
      this.scannerHooks.addDestroyable(
        addEventListener4(
          this.media,
          "webkitplaybacktargetavailabilitychanged",
          (event) => {
            const availability = event.availability;
            logger8.log("Scan result:", availability);
            const hasTargets = availability === "available";
            if (this.hasTargets !== hasTargets) {
              this.hasTargets = hasTargets;
              if (this.hasMedia) {
                this.stateChangedCallback("isAvailable");
              }
            }
            if (hasTargets && options?.stopWhenAvailable) {
              logger8.log(`Stopping scan because targets.`);
              this.stopAirPlayTargetScan();
            }
          }
        )
      );
      this.stateChangedCallback("isScanning");
      if (options?.timeout) {
        logger8.log(`Scanning for max ${options.timeout} ms.`);
        this.scannerHooks.addDestroyable(startTimeout2(this.stopAirPlayTargetScan.bind(this), options.timeout));
      }
    }
  }
  stopAirPlayTargetScan() {
    if (this.scannerHooks !== void 0) {
      this.scannerHooks.destroy();
      this.scannerHooks = void 0;
      this.stateChangedCallback("isScanning");
    }
  }
  enterAirPlay() {
    this.media.webkitShowPlaybackTargetPicker?.();
  }
  checkIfHasMedia() {
    const hasMedia = !this.isActive && !this.media.webkitWirelessVideoPlaybackDisabled;
    if (this.hasMedia !== hasMedia) {
      this.hasMedia = hasMedia;
      if (this.hasTargets) {
        this.stateChangedCallback("isAvailable");
      }
    }
  }
  checkIfActive() {
    if (this.media.webkitCurrentPlaybackTargetIsWireless !== this.isActive) {
      this.isActive = !this.isActive;
      this.stateChangedCallback("isActive");
    }
    this.checkIfHasMedia();
  }
};

// src/display-modes/webkit-pip.ts
import { DestroyableSet as DestroyableSet5, destroyableEventListener as addEventListener5 } from "@nrk/destroyable";

// src/display-modes/defs.ts
var WebkitPresentationMode = {
  Inline: "inline",
  Fullscreen: "fullscreen",
  PiP: "picture-in-picture"
};

// src/display-modes/webkit-pip.ts
var WebkitPiP = class extends DestroyableSet5 {
  video;
  stateChangedCallback;
  isAvailable = false;
  isActive = false;
  constructor(video, stateChangedCallback) {
    super();
    this.video = video;
    this.stateChangedCallback = stateChangedCallback;
    const checkIfActive = this.checkIfActive.bind(this);
    const checkIfAvailable = this.checkIfAvailable.bind(this);
    this.addDestroyables([
      addEventListener5(video, "webkitbeginfullscreen", checkIfActive),
      addEventListener5(video, "webkitendfullscreen", checkIfActive),
      addEventListener5(video, "resize", checkIfActive),
      addEventListener5(video, "loadedmetadata", checkIfAvailable),
      addEventListener5(video, "emptied", checkIfAvailable)
    ]);
    this.checkIfActive();
  }
  static isSupported(video) {
    return typeof video.webkitSupportsPresentationMode === "function" && !/iPhone|iPod/.exec(window.navigator.userAgent);
  }
  getPiPState() {
    return {
      isSupported: true,
      isAvailable: this.isAvailable,
      isActive: this.isActive
    };
  }
  enterPiP() {
    this.video.webkitSetPresentationMode?.(WebkitPresentationMode.PiP);
  }
  exitPiP() {
    this.video.webkitSetPresentationMode?.(WebkitPresentationMode.Inline);
  }
  checkIfAvailable() {
    const isAvailable = (
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.video.webkitSupportsPresentationMode(WebkitPresentationMode.PiP) && (this.video.webkitPresentationMode === WebkitPresentationMode.Inline || this.video.webkitPresentationMode === WebkitPresentationMode.PiP)
    );
    if (this.isAvailable !== isAvailable) {
      this.isAvailable = isAvailable;
      this.stateChangedCallback("isAvailable");
    }
  }
  checkIfActive() {
    const isActive = this.video.webkitPresentationMode === WebkitPresentationMode.PiP;
    if (this.isActive !== isActive) {
      this.isActive = isActive;
      this.stateChangedCallback("isActive");
    }
    this.checkIfAvailable();
  }
};

// src/display-modes/webkit-video-fullscreen.ts
import { DestroyableSet as DestroyableSet6, destroyableEventListener as addEventListener6 } from "@nrk/destroyable";
var WebkitVideoFullscreen = class extends DestroyableSet6 {
  video;
  stateChangedCallback;
  isAvailable = false;
  isActive = false;
  constructor(video, stateChangedCallback) {
    super();
    this.video = video;
    this.stateChangedCallback = stateChangedCallback;
    const checkIfActive = this.checkIfActive.bind(this);
    const checkIfAvailable = this.checkIfAvailable.bind(this);
    this.addDestroyables([
      addEventListener6(video, "webkitfullscreenchange", checkIfActive),
      addEventListener6(video, "webkitbeginfullscreen", checkIfActive),
      addEventListener6(video, "webkitendfullscreen", checkIfActive),
      addEventListener6(video, "resize", checkIfActive),
      addEventListener6(video, "loadedmetadata", checkIfAvailable),
      addEventListener6(video, "emptied", checkIfAvailable)
    ]);
    this.checkIfActive();
  }
  static isSupported(video) {
    return typeof video.webkitEnterFullscreen === "function" || typeof video.webkitEnterFullScreen === "function";
  }
  getFullscreenState() {
    return {
      isSupported: true,
      isAvailable: this.isAvailable,
      isActive: this.isActive,
      isNativeUI: true
    };
  }
  enterFullscreen() {
    this.video.webkitEnterFullscreen ? this.video.webkitEnterFullscreen() : this.video.webkitEnterFullScreen?.();
  }
  exitFullscreen() {
    this.video.webkitExitFullscreen ? this.video.webkitExitFullscreen() : this.video.webkitExitFullScreen?.();
  }
  checkIfAvailable() {
    const isAvailable = !!this.video.webkitSupportsFullscreen && (!this.video.webkitPresentationMode || this.video.webkitPresentationMode === WebkitPresentationMode.Inline || this.video.webkitPresentationMode === WebkitPresentationMode.Fullscreen);
    if (this.isAvailable !== isAvailable) {
      this.isAvailable = isAvailable;
      this.stateChangedCallback("isAvailable");
    }
  }
  checkIfActive() {
    const isActive = this.video.webkitPresentationMode === WebkitPresentationMode.Fullscreen;
    if (this.isActive !== isActive) {
      this.isActive = isActive;
      this.stateChangedCallback("isActive");
    }
    this.checkIfAvailable();
  }
};

// src/display-modes/local-display-modes.ts
var logger9 = getLogger("HTMLDisplayModes");
var LocalDisplayModes = class {
  eventEmitter;
  nullAPI;
  fullscreen;
  pip;
  airPlay;
  remotePlayback;
  constructor(eventEmitter, containerElement, mediaElement, options) {
    this.eventEmitter = eventEmitter;
    this.nullAPI = new NullDisplayModes();
    if (Fullscreen.isSupported()) {
      this.fullscreen = new Fullscreen(containerElement, this.handleFullscreenStateChanged.bind(this));
      logger9.log("Fullscreen is supported.");
    }
    if (mediaElement instanceof HTMLVideoElement) {
      if (!this.fullscreen && WebkitVideoFullscreen.isSupported(mediaElement)) {
        this.fullscreen = new WebkitVideoFullscreen(mediaElement, this.handleFullscreenStateChanged.bind(this));
        logger9.log("WebKit video fullscreen is supported");
      }
      if (PiP.isSupported()) {
        this.pip = new PiP(mediaElement, this.handlePiPStateChanged.bind(this));
        logger9.log("PiP is supported.");
      } else if (WebkitPiP.isSupported(mediaElement)) {
        this.pip = new WebkitPiP(mediaElement, this.handlePiPStateChanged.bind(this));
        logger9.log("WebKit PiP is supported.");
      }
    }
    if (mediaElement) {
      if (WebkitAirPlay.isSupported()) {
        this.airPlay = new WebkitAirPlay(mediaElement, this.handleAirPlayStateChanged.bind(this));
        logger9.log("WebKit AirPlay is supported.");
      }
      if (options?.enableNativeRemotePlayback && NativeRemotePlayback.isSupported(mediaElement)) {
        this.remotePlayback = new NativeRemotePlayback(mediaElement, this.handleRemotePlaybackStateChanged.bind(this));
        logger9.log("RemotePlayback is supported.");
      }
    }
  }
  // Fullscreen.
  getFullscreenState() {
    return this.fullscreen?.getFullscreenState() ?? this.nullAPI.getFullscreenState();
  }
  enterFullscreen(options) {
    if (this.pip?.getPiPState().isActive) {
      this.pip.exitPiP();
    }
    this.fullscreen?.enterFullscreen(options);
  }
  exitFullscreen() {
    this.fullscreen?.exitFullscreen();
  }
  // PiP.
  getPiPState() {
    return this.pip?.getPiPState() ?? this.nullAPI.getPiPState();
  }
  enterPiP() {
    if (this.fullscreen?.getFullscreenState().isActive) {
      this.fullscreen.exitFullscreen();
    }
    this.pip?.enterPiP();
  }
  exitPiP() {
    this.pip?.exitPiP();
  }
  // AirPlay.
  getAirPlayState() {
    return this.airPlay?.getAirPlayState() ?? this.nullAPI.getAirPlayState();
  }
  startAirPlayTargetScan(options) {
    this.airPlay?.startAirPlayTargetScan(options);
  }
  stopAirPlayTargetScan() {
    this.airPlay?.stopAirPlayTargetScan();
  }
  enterAirPlay() {
    this.airPlay?.enterAirPlay();
  }
  // RemotePlayback.
  getRemotePlaybackState() {
    return this.remotePlayback?.getRemotePlaybackState() ?? this.nullAPI.getRemotePlaybackState();
  }
  startRemoteTargetScan(options) {
    this.remotePlayback?.startRemoteTargetScan(options);
  }
  stopRemoteTargetScan() {
    this.remotePlayback?.stopRemoteTargetScan();
  }
  enterRemotePlayback() {
    this.remotePlayback?.enterRemotePlayback();
  }
  destroy() {
    this.fullscreen?.destroy();
    this.fullscreen = void 0;
    this.pip?.destroy();
    this.pip = void 0;
    this.airPlay?.destroy();
    this.airPlay = void 0;
    this.remotePlayback?.destroy();
    this.remotePlayback = void 0;
  }
  handleFullscreenStateChanged(field) {
    if (this.fullscreen) {
      const state = this.fullscreen.getFullscreenState();
      logger9.log(`Fullscreen: ${field} is ${state[field]}.`);
      this.eventEmitter.emit("playercore.fullscreenstatechange" /* FullscreenStateChange */, { state, field });
    }
  }
  handlePiPStateChanged(field) {
    if (this.pip) {
      const state = this.pip.getPiPState();
      logger9.log(`PiP: ${field} is ${state[field]}.`);
      this.eventEmitter.emit("playercore.pipstatechange" /* PiPStateChange */, { state, field });
    }
  }
  handleAirPlayStateChanged(field) {
    if (this.airPlay) {
      const state = this.airPlay.getAirPlayState();
      logger9.log(`AirPlay: ${field} is ${state[field]}.`);
      this.eventEmitter.emit("playercore.airplaystatechange" /* AirPlayStateChange */, { state, field });
    }
  }
  handleRemotePlaybackStateChanged(field) {
    if (this.remotePlayback) {
      const state = this.remotePlayback.getRemotePlaybackState();
      logger9.log(`RemotePlayback: ${field} is ${state[field]}.`);
      this.eventEmitter.emit("playercore.remoteplaybackstatechange" /* RemotePlaybackStateChange */, { state, field });
    }
  }
};

// src/core/media-element.ts
function getMediaElement(providedElement, options) {
  const mediaElement = resolveMediaElement(providedElement, options?.type ?? "video");
  applyMediaElementOptions(mediaElement, options);
  return mediaElement;
}
function resolveMediaElement(providedElement, type) {
  if (providedElement instanceof HTMLMediaElement) {
    return providedElement;
  }
  const existingElement = providedElement.querySelector(type);
  if (existingElement) {
    return existingElement;
  }
  const newElement = document.createElement(type);
  providedElement.insertBefore(newElement, providedElement.firstChild);
  return newElement;
}
function applyMediaElementOptions(mediaElement, options) {
  if (options?.crossOrigin !== void 0 && options.crossOrigin !== "none") {
    mediaElement.crossOrigin = options.crossOrigin;
  } else {
    mediaElement.removeAttribute("crossorigin");
  }
  if (options?.playsInline === false) {
    mediaElement.removeAttribute("playsinline");
    mediaElement.removeAttribute("webkit-playsinline");
  } else {
    mediaElement.setAttribute("playsinline", "");
    mediaElement.setAttribute("webkit-playsinline", "");
  }
  if (options?.videoPoster && mediaElement instanceof HTMLVideoElement) {
    mediaElement.poster = options.videoPoster;
  }
}

// src/core/object-element.ts
function getObjectElement(providedElement, attrs) {
  const objectElement = resolveObjectElement(providedElement, attrs.type);
  Object.getOwnPropertyNames(attrs).forEach((key) => {
    objectElement.setAttribute(key, `${attrs[key]}`);
  });
  return objectElement;
}
function resolveObjectElement(providedElement, type) {
  if (providedElement instanceof HTMLObjectElement && providedElement.type === type) {
    return providedElement;
  }
  const existingElement = providedElement.querySelector(`object[type="${type}"]`);
  if (existingElement) {
    return existingElement;
  }
  const newElement = document.createElement("object");
  newElement.type = type;
  providedElement.insertBefore(newElement, providedElement.firstChild);
  return newElement;
}

// src/core/player-core.ts
var logger10 = getLogger("PlayerCore");
var PlayerCore = class {
  playerElement;
  configuredAdapters;
  options;
  /**
   * @param playerElement The HTML element which should emit custom events, go
   * fullscreen and contain the media element, or an object specifying each of
   * those elements.
   *
   * @param configuredAdapters A prioritized list of adapters and their
   * configurations to select from when creating new sessions. Note: Difference
   * devices might have different preferences.
   *
   * @param options
   */
  constructor(playerElement, configuredAdapters, options) {
    this.playerElement = playerElement;
    this.configuredAdapters = configuredAdapters;
    this.options = options;
  }
  canPlayType(mediaType) {
    return this.findSupportedAdapter(mediaType) !== void 0;
  }
  /**
   * Create a playback session for a media URL. Only the media URL is required
   * in the media object. The media type is necessary if it's not obvious from
   * the media URL (file name extension).
   */
  createPlaybackSession(media, initialSessionState) {
    const resolvedMediaType = this.resolveMediaType(media.url, media.mediaType);
    if (resolvedMediaType === void 0) {
      throw new Error("Unable to detect media type of ${media.url}. Please specify.");
    }
    const configuredAdapter = this.findSupportedAdapter(resolvedMediaType);
    if (configuredAdapter === void 0) {
      throw new Error(`No matching adapter for ${toMediaTypeString(resolvedMediaType)}.`);
    }
    const adapterClass = configuredAdapter.adapterClass;
    logger10.log(`Using adapter:`, adapterClass.getAdapterInfo());
    const eventElement = this.playerElement instanceof HTMLElement ? this.playerElement : this.playerElement.eventElement;
    const externalEventEmitter = new CoreEventEmitter();
    const customEventEmitter = new CoreCustomEventEmitter(eventElement);
    const eventEmitter = new MultiEmitEventEmitter([externalEventEmitter, customEventEmitter]);
    const qualityTracker = new QualityTracker(eventEmitter);
    const providedElement = this.playerElement instanceof HTMLElement ? this.playerElement : this.playerElement.mediaElement;
    let mediaElement;
    if (adapterClass.getAdapterInfo().id === "AVPlay") {
      mediaElement = getObjectElement(providedElement, { type: "application/avplayer" });
    } else {
      const mediaElementOptions = {
        type: this.resolvePlayerType(resolvedMediaType),
        ...this.options?.mediaElementOptions,
        ...initialSessionState
        // Override videoPoster, if set.
      };
      mediaElement = getMediaElement(providedElement, mediaElementOptions);
    }
    const fullscreenElement = this.playerElement instanceof HTMLElement ? this.playerElement : this.playerElement.fullscreenElement;
    const displayModes = new LocalDisplayModes(
      eventEmitter,
      fullscreenElement,
      mediaElement instanceof HTMLMediaElement ? mediaElement : void 0,
      this.options?.displayModesOptions
    );
    const adapterContext = {
      mediaElement,
      eventEmitter,
      qualityTracker,
      media: {
        ...media,
        mediaType: resolvedMediaType
      },
      initialPlaybackState: initialSessionState,
      liveOptions: this.options?.liveOptions,
      displaySubtitles: this.options?.displaySubtitles
    };
    const adapter = new adapterClass(adapterContext, ...configuredAdapter.adapterConfig);
    const session = new PlaybackSession(adapter, eventEmitter, externalEventEmitter, qualityTracker, displayModes);
    new ScrubberUpdateEvents(session, eventEmitter, this.options?.liveOptions?.liveScrubberUpdateInterval);
    return Promise.resolve(session);
  }
  resolveMediaType(mediaUrl, mediaType) {
    return mediaType !== void 0 ? typeof mediaType === "string" ? parseMediaTypeString(mediaType) : mediaType : guessMediaTypeOfUrl(mediaUrl);
  }
  resolvePlayerType(mediaType) {
    if (mediaType) {
      return isAudioOnlyMediaType(mediaType) ? "audio" : "video";
    }
    return "video";
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  findSupportedAdapter(mediaType) {
    return this.configuredAdapters.find((adapter) => adapter.adapterClass.canPlayType(mediaType));
  }
};

export {
  ConfiguredAdapter,
  CoreCustomEventEmitter,
  CoreEventEmitter,
  MultiEmitEventEmitter,
  NullDisplayModes,
  PlaybackSession,
  QualityTracker,
  ScrubberUpdateEvents,
  PlayerCore
};
